import { Link } from "react-router-dom";
import SharedLayout from "../components/SharedLayout";
import useSEO from "../utils/useSEO";
import { HeroIllo, AIIllo } from "../components/AnimatedIllustrations";

const ArrowRight = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
    <path d="M5 12h14M12 5l7 7-7 7" />
  </svg>
);
const Check = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="w-4 h-4">
    <path d="M5 13l4 4L19 7" />
  </svg>
);
const GlowOrb = ({ className }) => (
  <div className={`absolute rounded-full blur-3xl opacity-20 pointer-events-none ${className}`} />
);

const CAPABILITIES = [
  {
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6">
        <path d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 0 0-2.456 2.456z" />
      </svg>
    ),
    title: "LLM Integration",
    desc: "Production-grade GPT-4, Claude, and Gemini integrations with prompt engineering, output validation, cost controls, and fallback strategies.",
    color: "border-amber-500/30 bg-amber-500/5",
    accent: "text-amber-400",
  },
  {
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6">
        <path d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607zM10.5 7.5v6m3-3h-6" />
      </svg>
    ),
    title: "RAG Systems",
    desc: "Retrieval-Augmented Generation pipelines over your own docs, databases, and knowledge bases — accurate answers, cited sources, no hallucinations.",
    color: "border-yellow-500/30 bg-yellow-500/5",
    accent: "text-yellow-400",
  },
  {
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6">
        <path d="M8.25 3v1.5M4.5 8.25H3m18 0h-1.5M4.5 12H3m18 0h-1.5m-15 3.75H3m18 0h-1.5M8.25 19.5V21M12 3v1.5m0 15V21m3.75-18v1.5m0 15V21m-9-1.5h10.5a2.25 2.25 0 0 0 2.25-2.25V6.75a2.25 2.25 0 0 0-2.25-2.25H6.75A2.25 2.25 0 0 0 4.5 6.75v10.5a2.25 2.25 0 0 0 2.25 2.25zm.75-12h9v9h-9v-9z" />
      </svg>
    ),
    title: "AI Agents",
    desc: "Multi-step autonomous agents with tool use, memory, planning loops, and human-in-the-loop checkpoints for complex, long-horizon workflows.",
    color: "border-orange-500/30 bg-orange-500/5",
    accent: "text-orange-400",
  },
  {
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6">
        <path d="M10.5 6h9.75M10.5 6a1.5 1.5 0 1 1-3 0m3 0a1.5 1.5 0 1 0-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-9.75 0h9.75" />
      </svg>
    ),
    title: "Workflow Automation",
    desc: "End-to-end process automation connecting your APIs, databases, and SaaS tools — eliminating manual work at scale without sacrificing reliability.",
    color: "border-red-500/30 bg-red-500/5",
    accent: "text-red-400",
  },
  {
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6">
        <path d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V4.125z" />
      </svg>
    ),
    title: "ML Pipelines",
    desc: "Data ingestion, feature engineering, model training, evaluation, and serving pipelines — from notebook prototype to monitored production system.",
    color: "border-rose-500/30 bg-rose-500/5",
    accent: "text-rose-400",
  },
  {
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6">
        <path d="M12 9v3.75m0-10.036A11.959 11.959 0 0 1 3.598 6 11.99 11.99 0 0 0 3 9.75c0 5.592 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.57-.598-3.75h-.152c-3.196 0-6.1-1.249-8.25-3.286zm0 13.036h.008v.008H12v-.008z" />
      </svg>
    ),
    title: "AI Safety & Guardrails",
    desc: "Prompt injection defences, output filtering, PII redaction, moderation layers, and audit logging — AI in production with guardrails you can trust.",
    color: "border-violet-500/30 bg-violet-500/5",
    accent: "text-violet-400",
  },
];

const TECH = [
  "OpenAI GPT-4", "Anthropic Claude", "LangChain", "LlamaIndex",
  "Pinecone", "Weaviate", "Python", "FastAPI", "Celery",
  "Hugging Face", "n8n", "Zapier",
];

const PROCESS = [
  {
    step: "01",
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6">
        <path d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607z" />
      </svg>
    ),
    title: "Problem Scoping",
    desc: "We identify where AI adds real ROI — not where it's trendy. Use-case validation, data audit, and feasibility assessment before any build.",
  },
  {
    step: "02",
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6">
        <path d="M9.75 3.104v5.714a2.25 2.25 0 0 1-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 0 1 4.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M19.8 15.3l-1.57.393A9.065 9.065 0 0 1 12 15a9.065 9.065 0 0 1-6.23-.693L4.2 13.9m15.6 1.4L21 18m-1.2-2.7-.566-.141M4.2 13.9 3 18m1.2-4.1.566-.141" />
      </svg>
    ),
    title: "Proof of Concept",
    desc: "A working prototype in days, not weeks. We test your specific data and edge cases before committing to a full build.",
  },
  {
    step: "03",
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6">
        <path d="M5.25 14.25h13.5m-13.5 0a3 3 0 0 1-3-3m3 3a3 3 0 1 0 0 6h13.5a3 3 0 1 0 0-6m-16.5-3a3 3 0 0 1 3-3h13.5a3 3 0 0 1 3 3m-19.5 0a4.5 4.5 0 0 1 .9-2.7L5.737 5.1a3.375 3.375 0 0 1 2.7-1.35h7.126c1.062 0 2.062.5 2.7 1.35l2.587 3.45a4.5 4.5 0 0 1 .9 2.7m0 0a3 3 0 0 1-3 3m0 3h.008v.008h-.008v-.008zm0-6h.008v.008h-.008v-.008zm-3 6h.008v.008h-.008v-.008zm0-6h.008v.008h-.008v-.008z" />
      </svg>
    ),
    title: "Production Pipeline",
    desc: "Scalable, monitored inference infrastructure with latency budgets, cost controls, and observability baked in.",
  },
  {
    step: "04",
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6">
        <path d="M15.75 15.75l-2.489-2.489m0 0a3.375 3.375 0 1 0-4.773-4.773 3.375 3.375 0 0 0 4.774 4.774zM21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0z" />
      </svg>
    ),
    title: "Evaluation & Evals",
    desc: "LLM-specific evaluation frameworks (RAGAs, custom evals) to measure quality — not just vibes.",
  },
  {
    step: "05",
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6">
        <path d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
      </svg>
    ),
    title: "Continuous Improvement",
    desc: "Drift monitoring, feedback loops, and model version management — so your AI gets better over time, not worse.",
  },
];

const FEATURES = [
  "Structured output with JSON schema",
  "Prompt versioning & A/B testing",
  "Cost monitoring & token budgets",
  "Streaming responses",
  "Semantic caching (reduce API costs)",
  "Vector search at scale",
  "Human-in-the-loop checkpoints",
  "PII detection & redaction",
  "Audit logging for all AI calls",
  "Multi-model fallback strategies",
  "RAG with source citations",
  "Custom evaluation framework",
];

export default function AIAutomationDetail() {
  useSEO({
    title: "AI Automation Agency in Texas | BerryByte",
    description: "BerryByte is an AI automation agency in Texas — AI process automation, LLM integrations & AI solutions for small businesses. Automate workflows and scale.",
    keywords: "ai automation agency texas, ai solutions for small businesses in texas, ai process automation texas, ai automation services texas, business process automation, workflow automation",
    canonical: "https://www.berrybyteinn.com/services/ai-automation",
  });

  return (
    <SharedLayout>
      <section className="relative min-h-[70vh] flex items-center overflow-hidden pt-20">
        <GlowOrb className="w-[700px] h-[700px] bg-amber-600 -top-32 -left-40" />
        <GlowOrb className="w-[500px] h-[500px] bg-orange-700 bottom-0 -right-32" />
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)", backgroundSize: "80px 80px" }} />

        {/* decorative animated illustration */}
        <HeroIllo>
          <AIIllo />
        </HeroIllo>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-24">
          <a href="/services" className="inline-flex items-center gap-2 text-slate-500 hover:text-amber-400 text-sm font-medium transition-colors duration-200 mb-8">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4"><path d="M19 12H5M12 5l-7 7 7 7" /></svg>
            Services / AI & Automation
          </a>

          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-amber-500/30 bg-amber-500/10 text-amber-300 text-sm font-medium mb-6 backdrop-blur-sm">
              <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
              LLMs · RAG · AI Agents · Workflow Automation
            </div>
            <h1 className="text-5xl lg:text-7xl font-black text-white leading-tight mb-6">
              AI That Works<br />
              <span className="bg-gradient-to-r from-amber-400 via-orange-400 to-rose-400 bg-clip-text text-transparent">In Production.</span>
            </h1>
            <p className="text-xl text-slate-400 leading-relaxed max-w-2xl mb-10">
              Not demos. Not prototypes. We build production-grade AI integrations, intelligent agents, and workflow automation that eliminate manual work and give your product a genuine competitive edge.
            </p>
            <Link to="/contact" className="group inline-flex items-center justify-center gap-2 px-8 py-4 rounded-2xl bg-gradient-to-r from-amber-600 to-orange-600 text-white font-semibold shadow-2xl shadow-amber-900/40 hover:shadow-amber-900/60 hover:-translate-y-1 transition-all duration-300">
              Explore AI Solutions<span className="group-hover:translate-x-1 transition-transform duration-200"><ArrowRight /></span>
            </Link>

            <div className="mt-16 grid grid-cols-2 sm:grid-cols-4 gap-8 pt-12 border-t border-white/5">
              {[{ value: "30+", label: "AI Products Shipped" }, { value: "60%", label: "Avg Cost Saved" }, { value: "<3s", label: "Avg LLM Response" }, { value: "99%", label: "Uptime SLA" }].map((s) => (
                <div key={s.label} className="text-center">
                  <div className="text-2xl lg:text-3xl font-black text-white mb-1">{s.value}</div>
                  <div className="text-sm text-slate-500">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="relative py-24 bg-[#070b14] overflow-hidden">
        <GlowOrb className="w-[500px] h-[500px] bg-amber-700 -top-24 -right-32" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-14">
            <span className="text-amber-400 font-semibold text-sm tracking-widest uppercase">AI Capabilities</span>
            <h2 className="mt-4 text-4xl lg:text-5xl font-black text-white">Every AI discipline,<br /><span className="bg-gradient-to-r from-amber-400 to-orange-400 bg-clip-text text-transparent">production-grade.</span></h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {CAPABILITIES.map((item) => {
              const Icon = item.icon;
              return (
                <div key={item.title} className={`rounded-2xl border ${item.color} p-7 hover:-translate-y-1 transition-transform duration-300`}>
                  <div className={`inline-flex p-3 rounded-xl bg-white/5 border border-white/10 ${item.accent} mb-4`}><Icon /></div>
                  <h3 className={`text-base font-bold mb-2 ${item.accent}`}>{item.title}</h3>
                  <p className="text-slate-400 text-sm leading-relaxed">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="relative py-24 bg-[#050810] overflow-hidden">
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-14">
            <span className="text-orange-400 font-semibold text-sm tracking-widest uppercase">Our AI Process</span>
            <h2 className="mt-4 text-4xl lg:text-5xl font-black text-white">ROI first.<br /><span className="bg-gradient-to-r from-amber-400 to-orange-400 bg-clip-text text-transparent">Tech second.</span></h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-5">
            {PROCESS.map((step) => {
              const Icon = step.icon;
              return (
                <div key={step.step} className="rounded-2xl border border-white/8 bg-white/[0.02] p-6 hover:-translate-y-1 transition-transform duration-300">
                  <div className="inline-flex p-2 rounded-lg bg-amber-500/10 border border-amber-500/20 text-amber-400 mb-4"><Icon /></div>
                  <div className="text-xs font-bold text-amber-500 tracking-widest mb-2">{step.step}</div>
                  <h3 className="text-sm font-bold text-white mb-2">{step.title}</h3>
                  <p className="text-slate-400 text-xs leading-relaxed">{step.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="relative py-24 bg-[#070b14] overflow-hidden">
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div>
              <span className="text-amber-400 font-semibold text-sm tracking-widest uppercase">Technology</span>
              <h2 className="mt-4 text-3xl lg:text-4xl font-black text-white mb-8">The AI stack we trust.</h2>
              <div className="flex flex-wrap gap-3">
                {TECH.map((t) => (
                  <span key={t} className="px-4 py-2 rounded-xl border border-amber-500/20 bg-amber-500/5 text-amber-300 text-sm font-medium hover:bg-amber-500/10 transition-colors">
                    {t}
                  </span>
                ))}
              </div>
            </div>
            <div>
              <span className="text-orange-400 font-semibold text-sm tracking-widest uppercase">What's Included</span>
              <h2 className="mt-4 text-3xl lg:text-4xl font-black text-white mb-8">AI that ships safely.</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {FEATURES.map((f) => (
                  <div key={f} className="flex items-center gap-3">
                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-amber-500/20 border border-amber-500/30 flex items-center justify-center text-amber-400"><Check /></span>
                    <span className="text-slate-300 text-sm">{f}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="relative py-24 bg-[#070b14] overflow-hidden">
        <GlowOrb className="w-[700px] h-[700px] bg-amber-700 top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2" />
        <div className="relative max-w-3xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-4xl lg:text-6xl font-black text-white mb-6">Ready to add<br /><span className="bg-gradient-to-r from-amber-400 to-orange-400 bg-clip-text text-transparent">AI to your product?</span></h2>
          <p className="text-slate-400 text-lg mb-10 leading-relaxed">
            Book a free AI strategy call. We'll identify the highest-ROI AI use cases for your specific business.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="group inline-flex items-center justify-center gap-2 px-8 py-4 rounded-2xl bg-gradient-to-r from-amber-600 to-orange-600 text-white font-semibold shadow-2xl shadow-amber-900/40 hover:shadow-amber-900/60 hover:-translate-y-1 transition-all duration-300">
              Book an AI Strategy Call<span className="group-hover:translate-x-1 transition-transform duration-200"><ArrowRight /></span>
            </Link>
            <a href="/services" className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-2xl border border-white/10 text-white font-semibold bg-white/5 hover:bg-white/10 hover:-translate-y-1 transition-all duration-300">
              Explore Other Services
            </a>
          </div>
        </div>
      </section>
    </SharedLayout>
  );
}
